import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BankLogo } from './BankLogo'
import { BANKS } from '../lib/banks'
import { SideModal } from './SideModal'
import { PanelShell, PlnBarra, PlnBloco, stickyTh } from './PanelShell'
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiLoader, FiRefreshCw, FiStar, FiLink2,
  FiCopy, FiCheck, FiSave, FiInfo,
} from 'react-icons/fi'
import { BsBank2 } from 'react-icons/bs'

// Contas e Cobrança — fusão de "Outras contas" (CRUD de bank_account) com
// "Conta PJ e Cobrança" (dados da empresa, cobrança, limites Pix). As duas
// telas mexiam nos mesmos dados de conta (a aba "Contas para recebimento" era
// só uma leitura filtrada do que já aparece aqui na aba Contas) — unificadas
// pedido do usuário 2026-07-06.

type Tab = 'contas' | 'empresa' | 'cobranca' | 'pix'
const TABS: Array<{ k: Tab; label: string }> = [
  { k: 'contas', label: 'Contas' },
  { k: 'empresa', label: 'Empresa' },
  { k: 'cobranca', label: 'Cobrança' },
  { k: 'pix', label: 'Limites Pix' },
]

type AccType = 'corrente' | 'dinheiro' | 'pj_interna'
interface RecConfig { exact: boolean; approx: boolean }
interface Account {
  id: number
  name: string
  type: AccType
  bank_code: string | null
  bank_name: string | null
  agency: string | null
  account_number: string | null
  initial_balance: number | string | null // DECIMAL do MySQL chega como string
  is_pj_main: 0 | 1
  reconciliation_config: RecConfig | null
}
const TYPE_LABEL: Record<AccType, string> = {
  corrente: 'Conta corrente', dinheiro: 'Dinheiro', pj_interna: 'Conta PJ',
}

interface Charge {
  boleto_multa_pct: number; boleto_juros_dia: number; pix_multa_pct: number; pix_juros_dia: number
  honorarios_pct: number
  card_installments_enabled: boolean; card_max_installments: number; card_anticipation_days: number
}
interface PixLimit { category: string; day_limit: number; night_limit: number }
interface SettingsData { company: { name: string; cnpj: string | null }; pjAccount: Account | null; charge: Charge; pixLimits: PixLimit[] }

const PIX_LABEL: Record<string, string> = {
  pessoas: 'Pessoas', empresas: 'Empresas', fornecedores: 'Fornecedores de confiança',
  entre_contas: 'Entre minhas contas', agendado: 'Pix agendado',
}
const brl = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const input = 'neu-convex rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

export function ContasCobranca() {
  const { authenticatedFetch } = useAuth()
  const [tab, setTab] = useState<Tab>('contas')
  const [saved, setSaved] = useState<string | null>(null)

  // ── aba "Contas" ──
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accLoading, setAccLoading] = useState(true)
  const [accError, setAccError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState<{ acc?: Account } | null>(null)
  const [recModal, setRecModal] = useState<Account | null>(null)

  const loadAccounts = useCallback(async () => {
    setAccLoading(true); setAccError(null)
    try {
      const r = await authenticatedFetch(`/api/bank-accounts?search=${encodeURIComponent(search)}`)
      const d = await r.json()
      if (d.success) setAccounts(d.items)
      else setAccError(d.error || 'Falha ao carregar')
    } catch {
      setAccError('Erro de conexão')
    } finally {
      setAccLoading(false)
    }
  }, [authenticatedFetch, search])
  useEffect(() => { const t = setTimeout(loadAccounts, 250); return () => clearTimeout(t) }, [loadAccounts])

  const removeAccount = async (acc: Account) => {
    if (!window.confirm(`Excluir a conta "${acc.name}"?`)) return
    const r = await authenticatedFetch(`/api/bank-accounts/${acc.id}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha'); return }
    loadAccounts()
  }

  // ── abas "Empresa" / "Cobrança" / "Pix" ──
  const [settings, setSettings] = useState<SettingsData | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(true)

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true)
    try {
      const r = await authenticatedFetch('/api/settings/conta-pj')
      const d = await r.json()
      if (d.success) setSettings(d)
    } finally { setSettingsLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { loadSettings() }, [loadSettings])

  const flash = (msg: string) => { setSaved(msg); setTimeout(() => setSaved(null), 2500) }
  const put = async (path: string, body: unknown, okMsg: string) => {
    const r = await authenticatedFetch(path, { method: 'PUT', body: JSON.stringify(body) })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha ao salvar'); return }
    flash(okMsg); loadSettings()
  }

  return (
    <PanelShell
      titulo="Contas e Cobrança"
      sub="Contas financeiras, dados da empresa, regras de cobrança e limites Pix"
      icone={<BsBank2 />}
      acoes={<>
        {/* Sub-nav (âncoras) — PLANOS (21/10/2026): subiu para as ações do
            cabeçalho, junto do aviso de "salvo". */}
        {saved && <span className="text-sm text-primary flex items-center gap-1"><FiCheck className="w-3.5 h-3.5" /> {saved}</span>}
        <div className="neu-seg inline-flex items-center gap-1">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === t.k ? 'neu-relief bg-primary/10 text-primary' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </>}>

      {tab === 'contas' && (
        <ContasTab
          accounts={accounts} loading={accLoading} error={accError} search={search} setSearch={setSearch}
          onNew={() => setModal({})} onEdit={acc => setModal({ acc })} onRemove={removeAccount}
          onReconciliation={acc => setRecModal(acc)}
        />
      )}
      {tab === 'empresa' && !settingsLoading && settings && (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {/* PLANOS (21/10/2026, subtelas): Empresa/Cobrança/Pix = pilha de PlnBloco com gap 16 */}
          <DadosPJ data={settings} onSaveCompany={b => put('/api/settings/company', b, 'Dados salvos')} />
        </div>
      )}
      {tab === 'cobranca' && !settingsLoading && settings && (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          <Cobranca charge={settings.charge} onSave={b => put('/api/settings/charge-config', b, 'Cobrança salva')} />
        </div>
      )}
      {tab === 'pix' && !settingsLoading && settings && (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          <LimitesPix limits={settings.pixLimits} onSave={limits => put('/api/settings/pix-limits', { limits }, 'Limites salvos')} />
        </div>
      )}
      {(tab !== 'contas' && settingsLoading) && (
        <div className="flex-1 flex items-center justify-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin" /></div>
      )}

      {modal && <AccountModal acc={modal.acc} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAccounts(); loadSettings() }} />}
      {recModal && <ReconciliationModal acc={recModal} onClose={() => setRecModal(null)} onSaved={() => { setRecModal(null); loadAccounts() }} />}
    </PanelShell>
  )
}

// ── Aba: Contas (CRUD de bank_account) ──
function ContasTab({ accounts, loading, error, search, setSearch, onNew, onEdit, onRemove, onReconciliation }: {
  accounts: Account[]; loading: boolean; error: string | null; search: string; setSearch: (s: string) => void
  onNew: () => void; onEdit: (acc: Account) => void; onRemove: (acc: Account) => void; onReconciliation: (acc: Account) => void
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      {/* PLANOS (21/10/2026): busca + "Nova conta" numa PlnBarra; tabela num PlnBloco. */}
      <PlnBarra>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar conta…"
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
        </div>
        <button onClick={onNew}
          className="neu-relief ml-auto inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:opacity-90">
          <FiPlus className="w-4 h-4" /> Nova conta financeira
        </button>
      </PlnBarra>

      {error && <div className="shrink-0 neu-relief rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400">{error}</div>}

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell) */}
      <PlnBloco
        titulo="Contas financeiras"
        nota={loading ? undefined : `${accounts.length} conta(s) · saldo inicial e conciliação automática de cada uma`}
        className="flex-1 min-h-[280px]"
      >
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Conta</th>
              <th className={`px-4 py-2.5 font-medium w-32 ${stickyTh}`}>Tipo</th>
              <th className={`px-4 py-2.5 font-medium w-32 text-right ${stickyTh}`} title="Saldo da conta antes do primeiro lançamento registrado no sistema">Saldo inicial</th>
              <th className={`px-4 py-2.5 font-medium w-56 ${stickyTh}`}>Conciliação automática</th>
              <th className={`px-4 py-2.5 font-medium w-24 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Nenhuma conta cadastrada.</td></tr>
            ) : accounts.map(acc => {
              const rc = acc.reconciliation_config
              return (
                <tr key={acc.id} className="neu-tr border-b border-border/60 last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <BankLogo code={acc.bank_code} name={acc.bank_name || acc.name} type={acc.type} size={36} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-foreground font-medium truncate">{acc.name}</span>
                          {acc.is_pj_main === 1 && (
                            <span title="Conta PJ principal" className="neu-tag inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600 bg-amber-500/10 rounded-full px-1.5 py-0.5">
                              <FiStar className="w-2.5 h-2.5" /> PJ
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {acc.bank_name || '—'}{acc.agency ? ` · Ag. ${acc.agency}` : ''}{acc.account_number ? ` · Cc. ${acc.account_number}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">{TYPE_LABEL[acc.type]}</span>
                  </td>
                  <td className="px-4 py-3 text-right" title="Saldo da conta antes do primeiro lançamento registrado no sistema">
                    <span className={`text-sm tabular-nums ${Number(acc.initial_balance || 0) < 0 ? 'text-red-500' : 'text-foreground'}`}>
                      R$ {brl(Number(acc.initial_balance || 0))}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {rc && (rc.exact || rc.approx) ? (
                        <div className="flex flex-wrap gap-1">
                          {rc.exact && <span className="neu-tag text-xs rounded-full px-2 py-0.5 bg-primary/10 text-primary">Exata</span>}
                          {rc.approx && <span className="neu-tag text-xs rounded-full px-2 py-0.5 bg-primary/10 text-primary">Aproximada</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Não configurada</span>
                      )}
                      <button onClick={() => onReconciliation(acc)} title="Configurar conciliação"
                        className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <FiRefreshCw className="w-3 h-3" /> Configurar
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Editar" onClick={() => onEdit(acc)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"><FiEdit2 className="w-3.5 h-3.5" /></button>
                      <button title="Excluir" onClick={() => onRemove(acc)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"><FiTrash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      </PlnBloco>
    </div>
  )
}

function AccountModal({ acc, onClose, onSaved }: { acc?: Account; onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [name, setName] = useState(acc?.name || '')
  const [type, setType] = useState<AccType>(acc?.type || 'corrente')
  const [bankCode, setBankCode] = useState(acc?.bank_code || '')
  const [agency, setAgency] = useState(acc?.agency || '')
  const [accountNumber, setAccountNumber] = useState(acc?.account_number || '')
  // Saldo inicial: string no input (aceita vírgula pt-BR), número no body.
  const [initialBalance, setInitialBalance] = useState(acc?.initial_balance != null ? String(Number(acc.initial_balance)) : '0')
  const [isPjMain, setIsPjMain] = useState(acc?.is_pj_main === 1)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    if (!name.trim()) { setErr('Informe o nome da conta.'); return }
    // aceita "1234.56" e o formato pt-BR "1.234,56"
    const raw = String(initialBalance).trim()
    const ib = raw === '' ? 0 : Number(raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw)
    if (!Number.isFinite(ib)) { setErr('Saldo inicial inválido.'); return }
    setSaving(true); setErr(null)
    const bank = BANKS.find(b => b.code === bankCode)
    const body = { name, type, bank_code: bankCode || null, bank_name: bank?.name || null, agency, account_number: accountNumber, initial_balance: ib, is_pj_main: isPjMain }
    try {
      const r = acc
        ? await authenticatedFetch(`/api/bank-accounts/${acc.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await authenticatedFetch('/api/bank-accounts', { method: 'POST', body: JSON.stringify(body) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      onSaved()
    } catch { setErr('Erro de conexão') } finally { setSaving(false) }
  }

  return (
    <SideModal
      title={acc ? 'Editar conta' : 'Nova conta financeira'}
      subtitle="Banco, caixa físico ou cofre"
      icon={BsBank2}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-relief h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </>
      }
    >
      {err && <div className="neu-relief rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">{err}</div>}
      {/* PLANOS (21/10/2026, subtelas): campos numa caixa com respiro */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome da conta*</label>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} className={input} placeholder="Ex.: Bradesco CC Principal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
              <select value={type} onChange={e => setType(e.target.value as AccType)} className={input}>
                <option value="corrente">Conta corrente</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="pj_interna">Conta PJ</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Banco</label>
              <select value={bankCode} onChange={e => setBankCode(e.target.value)} className={input}>
                <option value="">—</option>
                {BANKS.map(b => <option key={b.code} value={b.code}>{b.code} · {b.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Agência</label>
              <input value={agency} onChange={e => setAgency(e.target.value)} className={input} placeholder="0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Conta</label>
              <input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} className={input} placeholder="00000-0" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1" title="Saldo da conta antes do primeiro lançamento registrado no sistema — entra no saldo de abertura do Extrato de movimentações e do Fluxo de caixa">
              Saldo inicial (R$)
            </label>
            <input value={initialBalance} onChange={e => setInitialBalance(e.target.value)} className={input} placeholder="0,00" inputMode="decimal"
              title="Saldo da conta antes do primeiro lançamento registrado no sistema. Pode ser negativo (conta no vermelho)." />
            <p className="text-[12.5px] text-muted-foreground mt-1.5">Ponto de partida do caixa antes do primeiro lançamento registrado. Pode ser negativo.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer pt-1">
            <input type="checkbox" checked={isPjMain} onChange={e => setIsPjMain(e.target.checked)} className="neu-check rounded border-border" />
            Definir como conta PJ principal
          </label>
        </div>
    </SideModal>
  )
}

function ReconciliationModal({ acc, onClose, onSaved }: { acc: Account; onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [exact, setExact] = useState(!!acc.reconciliation_config?.exact)
  const [approx, setApprox] = useState(!!acc.reconciliation_config?.approx)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      await authenticatedFetch(`/api/bank-accounts/${acc.id}`, { method: 'PUT', body: JSON.stringify({ reconciliation_config: { exact, approx } }) })
      onSaved()
    } finally { setSaving(false) }
  }

  const Toggle = ({ on, set, title, desc }: { on: boolean; set: (v: boolean) => void; title: string; desc: string }) => (
    <button onClick={() => set(!on)} className="neu-convex w-full flex items-start gap-3 p-4 rounded-xl border border-border hover:bg-secondary/50 text-left transition-colors">
      <span className={`neu-switch mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-primary' : 'neu-switch-off bg-muted'}`}>
        <span className={`neu-knob inline-block h-4 w-4 mt-0.5 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </span>
      <span>
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-[12.5px] leading-snug text-muted-foreground mt-0.5">{desc}</span>
      </span>
    </button>
  )

  return (
    <SideModal
      title="Conciliação automática"
      subtitle={`Conta: ${acc.name}`}
      icon={FiLink2}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-relief h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Toggle on={exact} set={setExact} title="Correspondência exata" desc="Mesmo valor e mesma data de vencimento. Dá baixa sozinho, sem esperar clique." />
        <Toggle on={approx} set={setApprox} title="Correspondência aproximada" desc="Mesmo valor, vencimento em ±5 dias. Ainda não implementado — liga o toggle mas não muda o comportamento." />
      </div>
      <div className="neu-convex rounded-xl bg-secondary/40 border border-border p-4 text-[13px] leading-relaxed text-muted-foreground">
        Ao importar um extrato (OFX/CSV) desta conta, cada movimento com "Correspondência exata" é conciliado automaticamente; os demais entram pendentes na fila normal de conciliação.
      </div>
    </SideModal>
  )
}

// ── Aba: Empresa (dados PJ) ──
// PLANOS (21/10/2026): o cartão virou PlnBloco (título no cabeçalho do papel).
// Subtelas (21/10): sem mb-* — o espaço vem do gap da pilha (FormStack); nota/extra no cabeçalho.
function Card({ title, nota, extra, children }: { title: string; nota?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }) {
  return (
    <PlnBloco titulo={title} nota={nota} extra={extra}>
      <div className="p-5">{children}</div>
    </PlnBloco>
  )
}
const FormStack = ({ children }: { children: React.ReactNode }) => <div className="flex flex-col gap-4 pb-1">{children}</div>

function DadosPJ({ data, onSaveCompany }: { data: SettingsData; onSaveCompany: (b: { name: string; cnpj: string }) => void }) {
  const [name, setName] = useState(data.company.name)
  const [cnpj, setCnpj] = useState(data.company.cnpj || '')
  const [copied, setCopied] = useState(false)
  const acc = data.pjAccount

  const copy = () => {
    const txt = acc
      ? `${acc.bank_name || ''} — Ag. ${acc.agency || '—'} Cc. ${acc.account_number || '—'} — CNPJ ${cnpj || '—'}`
      : `CNPJ ${cnpj || '—'}`
    navigator.clipboard?.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  return (
    <FormStack>
      <Card title="Empresa" nota="Nome e CNPJ usados nos documentos de cobrança">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">Nome / Razão social</label>
            <input value={name} onChange={e => setName(e.target.value)} className={input} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">CNPJ</label>
            <input value={cnpj} onChange={e => setCnpj(e.target.value)} className={input} placeholder="00.000.000/0000-00" />
          </div>
        </div>
        <div className="flex justify-end mt-5">
          <button onClick={() => onSaveCompany({ name, cnpj })} className="neu-relief h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"><FiSave className="w-4 h-4" /> Salvar</button>
        </div>
      </Card>

      <Card title="Conta PJ principal" extra={acc ? (
        <button onClick={copy} className="neu-convex h-9 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 text-sm text-primary hover:bg-secondary">
          {copied ? <><FiCheck className="w-4 h-4" /> Copiado</> : <><FiCopy className="w-4 h-4" /> Copiar dados</>}
        </button>
      ) : undefined}>
        {acc ? (
          <div className="flex items-center gap-3">
            <BankLogo code={acc.bank_code} name={acc.bank_name || acc.name} type={acc.type} size={40} />
            <div className="flex-1">
              <div className="text-[15px] text-foreground font-medium">{acc.name}</div>
              <div className="text-sm text-muted-foreground">{acc.bank_name || '—'} · Ag. {acc.agency || '—'} · Cc. {acc.account_number || '—'}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><FiInfo className="w-4 h-4" /> Nenhuma conta marcada como PJ principal. Defina uma na aba <strong>Contas</strong>.</p>
        )}
      </Card>
    </FormStack>
  )
}

// ── Aba: Cobrança ──
function Cobranca({ charge, onSave }: { charge: Charge; onSave: (b: Charge) => void }) {
  const [c, setC] = useState<Charge>(charge)
  const set = (k: keyof Charge, v: number | boolean) => setC(prev => ({ ...prev, [k]: v }))
  // ⚠️ Função comum, e NÃO componente (<Field />): declarado aqui dentro, um componente
  // é recriado a cada tecla, o React remonta o <input> e o campo PERDE O FOCO — só o
  // primeiro dígito entrava. Em 13/10/2026 o diretor tentou mudar os honorários e
  // acabou salvando "1". O texto digitado fica em `txt` para aceitar "0,0" no meio do caminho.
  const [txt, setTxt] = useState<Partial<Record<keyof Charge, string>>>({})
  const field = (label: string, k: keyof Charge, suffix?: string) => (
    <div className="flex flex-col gap-1" key={k}>
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="flex items-center gap-1.5">
        <input type="text" inputMode="decimal" value={txt[k] ?? String(c[k] ?? '').replace('.', ',')}
          onChange={e => {
            const t = e.target.value.replace(/[^0-9.,]/g, '')
            setTxt(prev => ({ ...prev, [k]: t }))
            const n = Number(t.replace(',', '.'))
            if (t !== '' && Number.isFinite(n)) set(k, n)
          }}
          onBlur={() => setTxt(prev => { const n = { ...prev }; delete n[k]; return n })}
          className={`${input} w-28`} />
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  )
  return (
    <FormStack>
      <Card title="Boletos" nota="Multa e juros dos boletos vencidos">
        <div className="grid sm:grid-cols-2 gap-4">
          {field('Multa por atraso', 'boleto_multa_pct', '%')}
          {field('Juros por dia de atraso', 'boleto_juros_dia', '% ao dia')}
        </div>
      </Card>
      {/* Honorários advocatícios (13/10/2026, igual ao cob.twms): o percentual padrão.
          Quem atende liga a cobrança em cada negociação, na lateral do Conversas. */}
      <Card title="Honorários advocatícios">
        <div className="grid sm:grid-cols-2 gap-4">
          {field('Honorários do advogado', 'honorarios_pct', '% sobre o valor atualizado')}
        </div>
        <p className="text-[13px] leading-relaxed text-muted-foreground mt-4">Calculado em cima do valor atualizado (saldo + multa + juros acima) dos títulos vencidos. O total com honorários aparece na ficha do painel Cobrança e no topo da lateral do Conversas. No boleto só entra quando quem atende marca "Cobrar honorários advocatícios".</p>
      </Card>
      <Card title="Pix cobrança" nota="Multa e juros do Pix com vencimento">
        <div className="grid sm:grid-cols-2 gap-4">
          {field('Multa', 'pix_multa_pct', '%')}
          {field('Juros por dia', 'pix_juros_dia', '% ao dia')}
        </div>
      </Card>
      <Card title="Cartão de crédito via link">
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer mb-4">
          <input type="checkbox" checked={c.card_installments_enabled} onChange={e => set('card_installments_enabled', e.target.checked)} className="neu-check rounded border-border" />
          Permitir parcelamento
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          {field('Nº máximo de parcelas', 'card_max_installments')}
          {field('Antecipação (D+N)', 'card_anticipation_days', 'dias')}
        </div>
      </Card>
      {/* o salvar vale para os quatro blocos acima — mora numa faixa, não solto no chão */}
      <PlnBarra>
        <span className="text-[13px] text-muted-foreground">Salva os quatro blocos acima de uma vez</span>
        <button onClick={() => onSave(c)} className="neu-relief ml-auto h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"><FiSave className="w-4 h-4" /> Configurar cobrança</button>
      </PlnBarra>
    </FormStack>
  )
}

// ── Aba: Limites Pix ──
function LimitesPix({ limits, onSave }: { limits: PixLimit[]; onSave: (l: PixLimit[]) => void }) {
  const [rows, setRows] = useState<PixLimit[]>(limits)
  const set = (cat: string, field: 'day_limit' | 'night_limit', v: number) =>
    setRows(prev => prev.map(r => r.category === cat ? { ...r, [field]: v } : r))
  return (
    <FormStack>
    {/* PLANOS (21/10, subtelas): a explicação de horários virou a nota do bloco */}
    <Card title="Limites Pix por categoria" nota="Diurno: 6h–19h59 · Noturno: 20h–5h59. Alterações são versionadas (auditoria).">
      <div className="space-y-3">
        <div className="hidden sm:grid grid-cols-[1fr_140px_140px] gap-3 text-[12.5px] font-medium text-muted-foreground px-1">
          <span>Categoria</span><span>Limite diurno (R$)</span><span>Limite noturno (R$)</span>
        </div>
        {rows.map(r => (
          <div key={r.category} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px] gap-3 items-center">
            <span className="text-sm text-foreground">{PIX_LABEL[r.category]}</span>
            <input type="number" step="0.01" min="0" value={r.day_limit} onChange={e => set(r.category, 'day_limit', Number(e.target.value))} className={input} placeholder={brl(0)} />
            <input type="number" step="0.01" min="0" value={r.night_limit} onChange={e => set(r.category, 'night_limit', Number(e.target.value))} className={input} placeholder={brl(0)} />
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-5">
        <button onClick={() => onSave(rows)} className="neu-relief h-9 inline-flex items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"><FiSave className="w-4 h-4" /> Salvar limites</button>
      </div>
    </Card>
    </FormStack>
  )
}
